import { useState } from 'react';

const tools = [
  {
    id: 'armcare',
    name: 'ArmCare',
    icon: 'ri-heart-pulse-line',
    color: 'teal',
    tagline: 'Arm Health & Workload Management',
    description: 'Comprehensive arm health monitoring and throwing program management. Track workload, recovery, and performance metrics to optimize arm care protocols and prevent injury.',
    tags: ['Workload Tracking', 'Recovery Metrics', 'Injury Prevention'],
    img: 'https://static.readdy.ai/image/4abb71d54c7d60621cfc0b2fab4f7970/e240fe1103d105c0ef5b534e79ec2ec3.jpeg',
  },
  {
    id: 'rapsodo',
    name: 'Rapsodo',
    icon: 'ri-radar-line',
    color: 'teal',
    tagline: 'Pitch & Throw Analytics',
    description: 'Instant feedback on velocity, spin rate, break, and release metrics for every throw. See exactly what the ball is doing and why.',
    tags: ['Velocity', 'Spin Rate', 'Release Point'],
    img: 'https://static.readdy.ai/image/4abb71d54c7d60621cfc0b2fab4f7970/628373fde68b2f43e9946ecbc1eead94.webp',
  },
  {
    id: 'uplift',
    name: 'UpLift AI',
    icon: 'ri-brain-line',
    color: 'teal',
    tagline: 'AI-Powered Movement Analysis',
    description: 'AI-powered movement analysis and training optimization. Real-time feedback on exercise execution and adaptive program design based on your data.',
    tags: ['AI Coaching', 'Form Analysis', 'Program Adaptation'],
    img: 'https://public.readdy.ai/ai/img_res/edited_92022d1dbacca50c5e416c125e954149_e23a1249.jpg',
  },
  {
    id: 'axioforce',
    name: 'Axio Performance',
    icon: 'ri-speed-line',
    color: 'teal',
    tagline: 'Force Plate & Power Metrics',
    description: 'Force plate technology measuring ground reaction forces, power output, and asymmetries to optimize explosive movements and reduce injury risk.',
    tags: ['Ground Force', 'Power Output', 'Asymmetry Detection'],
    img: 'https://public.readdy.ai/ai/img_res/1d5e45ebe074bace8a7c225f740ee9be.jpg',
  },
  {
    id: 'coachnow',
    name: 'CoachNow',
    icon: 'ri-video-chat-line',
    color: 'orange',
    tagline: 'Remote Coaching & Video Analysis',
    description: 'Seamless athlete-coach communication platform for video analysis, feedback, and remote training. Stay connected and accountable between sessions.',
    tags: ['Video Analysis', 'Remote Coaching', 'Accountability'],
    img: 'https://static.readdy.ai/image/4abb71d54c7d60621cfc0b2fab4f7970/09aaef08e35943f7d961dd5726025e5c.png',
  },
  {
    id: 'vald',
    name: 'Vald Performance',
    icon: 'ri-scales-3-line',
    color: 'orange',
    tagline: 'Strength & Force Testing Systems',
    description: 'World-leading force testing and assessment technology. Measure hamstring strength, isometric force output, and bilateral asymmetries with clinical-grade accuracy to benchmark athletes and guide return-to-play decisions.',
    tags: ['Force Testing', 'Hamstring Strength', 'Asymmetry Analysis', 'Return-to-Play'],
    img: 'https://readdy.ai/api/search-image?query=professional%20sports%20science%20lab%20with%20athlete%20performing%20strength%20assessment%20on%20advanced%20force%20testing%20equipment%2C%20dark%20athletic%20facility%20with%20orange%20accent%20lighting%2C%20modern%20biomechanics%20testing%20setup%20with%20digital%20displays%20showing%20real-time%20force%20output%20graphs%20and%20athlete%20performance%20metrics%2C%20high%20contrast%20dramatic%20lighting&width=800&height=500&seq=vald-performance-card-001&orientation=landscape',
  },
  {
    id: 'smartspeed',
    name: 'Smart Speed',
    icon: 'ri-run-line',
    color: 'teal',
    tagline: 'Speed & Agility Timing Systems',
    description: 'Electronic timing gates and speed measurement technology delivering millisecond-accurate sprint times, acceleration data, and agility scores. Quantify every speed session and track athlete development over time with objective data.',
    tags: ['Sprint Timing', 'Acceleration', 'Agility Testing', 'Speed Development'],
    img: 'https://readdy.ai/api/search-image?query=athlete%20sprinting%20through%20electronic%20timing%20gates%20on%20a%20turf%20field%20inside%20a%20professional%20sports%20performance%20facility%2C%20dark%20dramatic%20lighting%20with%20teal%20accent%20illumination%2C%20motion%20blur%20on%20athlete%20showing%20explosive%20speed%2C%20timing%20sensors%20and%20digital%20display%20panels%20showing%20split%20times%20in%20background&width=800&height=500&seq=smartspeed-card-001&orientation=landscape',
  },
  {
    id: 'dynomat',
    name: 'Dynomat',
    icon: 'ri-pulse-line',
    color: 'orange',
    tagline: 'Dynamic Strength & Power Assessment',
    description: 'Advanced dynamometer system for measuring dynamic and isometric strength across multiple movement patterns. Capture peak force, rate of force development, and power output to build individualized strength profiles for every athlete.',
    tags: ['Peak Force', 'Rate of Force Development', 'Power Output', 'Strength Profiling'],
    img: 'https://readdy.ai/api/search-image?query=athlete%20connected%20to%20dynamometer%20strength%20testing%20device%20in%20a%20high-tech%20sports%20performance%20lab%2C%20orange%20accent%20lighting%20illuminating%20digital%20force%20measurement%20equipment%20and%20live%20data%20readouts%20on%20screens%2C%20dark%20industrial%20gym%20background%20with%20clean%20modern%20test%20station%2C%20dramatic%20low%20key%20lighting&width=800&height=500&seq=dynomat-card-001&orientation=landscape',
  },
];

const colorMap: Record<string, { border: string; glow: string; tag: string; tagText: string; icon: string; dot: string }> = {
  teal: {
    border: 'border-teal-500',
    glow: 'shadow-teal-500/30',
    tag: 'bg-teal-500/15 border border-teal-500/30',
    tagText: 'text-teal-400',
    icon: 'text-teal-400',
    dot: 'bg-teal-400',
  },
  orange: {
    border: 'border-orange-500',
    glow: 'shadow-orange-500/30',
    tag: 'bg-orange-500/15 border border-orange-500/30',
    tagText: 'text-orange-400',
    icon: 'text-orange-400',
    dot: 'bg-orange-400',
  },
};

export default function Technology() {
  const [active, setActive] = useState('armcare');
  const current = tools.find((t) => t.id === active) ?? tools[0];
  const c = colorMap[current.color];

  return (
    <section id="technology" className="py-20 md:py-28 bg-gray-950 relative overflow-hidden">
      {/* subtle background grid */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: 'linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)', backgroundSize: '48px 48px' }}
      />

      <div className="max-w-7xl mx-auto px-5 lg:px-12 relative z-10">

        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-teal-400 text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Powered by Industry-Leading Tech
          </span>
          <h2 className="text-4xl sm:text-5xl font-black text-white leading-tight">
            Precision Meets <span className="text-teal-400">Performance</span>
          </h2>
          <p className="mt-4 text-gray-400 text-base max-w-2xl mx-auto">
            Every rep, every throw, every session — backed by objective data from eight elite platforms.
          </p>
        </div>

        {/* Tab Pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {tools.map((t) => {
            const isActive = t.id === active;
            const tc = colorMap[t.color];
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                  isActive
                    ? `${tc.border} bg-gray-800 text-white shadow-lg ${tc.glow}`
                    : 'border-gray-700 bg-gray-900 text-gray-400 hover:border-gray-500 hover:text-gray-200'
                }`}
              >
                <span className={`w-5 h-5 flex items-center justify-center`}>
                  <i className={`${t.icon} text-sm ${isActive ? tc.icon : ''}`}></i>
                </span>
                {t.name}
              </button>
            );
          })}
        </div>

        {/* Main Card */}
        <div className={`rounded-3xl border ${c.border} bg-gray-900 ${c.glow} overflow-hidden transition-all duration-300`}>
          <div className="grid grid-cols-1 lg:grid-cols-2">

            {/* Left: Image */}
            <div className="relative h-64 sm:h-80 lg:h-auto min-h-[320px] rounded-tl-3xl rounded-bl-3xl rounded-tr-3xl lg:rounded-tr-none overflow-hidden">
              <img
                key={current.id}
                src={current.img}
                alt={current.name}
                className={`w-full h-full ${current.id === 'coachnow' ? 'object-contain bg-white p-8' : 'object-cover object-top'} transition-opacity duration-300`}
              />
              {/* floating name badge */}
              <div className="absolute top-5 left-5">
                <span className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider bg-gray-950/80 backdrop-blur-sm border ${c.border} ${c.tagText}`}>
                  {current.name}
                </span>
              </div>
            </div>

            {/* Right: Content */}
            <div className="p-8 sm:p-10 flex flex-col justify-center">
              <div className={`w-12 h-12 flex items-center justify-center rounded-2xl bg-gray-800 border ${c.border} mb-5`}>
                <i className={`${current.icon} text-xl ${c.icon}`}></i>
              </div>

              <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${c.tagText}`}>{current.tagline}</p>
              <h3 className="text-3xl sm:text-4xl font-black text-white mb-4">{current.name}</h3>
              <p className="text-gray-300 text-base leading-relaxed mb-7">{current.description}</p>

              <div className="flex flex-wrap gap-2">
                {current.tags.map((tag) => (
                  <span key={tag} className={`px-3 py-1.5 rounded-full text-xs font-bold ${c.tag} ${c.tagText} whitespace-nowrap`}>
                    {tag}
                  </span>
                ))}
              </div>

              {/* step dots */}
              <div className="flex items-center gap-2 mt-8">
                {tools.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setActive(t.id)}
                    className={`rounded-full transition-all duration-200 cursor-pointer ${
                      t.id === active ? `w-6 h-2 ${c.dot}` : 'w-2 h-2 bg-gray-600 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom stat strip */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: 'ri-database-2-line', label: 'Data Points Per Session', value: '500+' },
            { icon: 'ri-shield-check-line', label: 'Injury Risk Reduction', value: 'Proven' },
            { icon: 'ri-bar-chart-grouped-line', label: 'Metrics Tracked', value: '20+' },
            { icon: 'ri-time-line', label: 'Real-Time Feedback', value: 'Live' },
          ].map((stat) => (
            <div key={stat.label} className="bg-gray-900 border border-gray-800 rounded-2xl px-5 py-4 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-500/10 shrink-0">
                <i className={`${stat.icon} text-lg text-teal-400`}></i>
              </div>
              <div>
                <p className="text-white font-black text-lg leading-none">{stat.value}</p>
                <p className="text-gray-500 text-xs mt-0.5">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
